import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowUpRight, ArrowRight, Globe, Instagram } from "lucide-react";
import api from "@/lib/api";

const WorkDetail = () => {
    const { slug } = useParams();
    const [cs, setCs] = useState(null);
    const [all, setAll] = useState([]);

    useEffect(() => {
        api.get(`/case-studies/${slug}`).then((r) => setCs(r.data)).catch(() => setCs({ notfound: true }));
        api.get("/case-studies").then((r) => setAll(r.data)).catch(() => {});
    }, [slug]);

    if (!cs) return <div className="pt-40 st-container text-center text-[var(--st-ink-soft)]">Loading…</div>;
    if (cs.notfound) return (
        <main className="pt-40 pb-24 st-container">
            <h1 className="font-serif text-5xl">Not found.</h1>
            <p className="mt-4"><Link to="/work" className="link-underline">Back to all work</Link></p>
        </main>
    );

    // Next case study (loop back to first if at end)
    const currentIndex = all.findIndex((x) => x.slug === slug);
    const next = all.length > 0 ? all[(currentIndex + 1) % all.length] : null;

    return (
        <main data-testid="work-detail-page" className="bg-[var(--st-cream)]">
            {/* Back link */}
            <div className="pt-28 lg:pt-32 pb-4">
                <div className="st-container">
                    <Link to="/work" className="inline-flex items-center gap-2 eyebrow text-[var(--st-gold-deep)] hover:text-[var(--st-ink)]">
                        <ArrowLeft size={14} /> All work
                    </Link>
                </div>
            </div>

            {/* Header — client, tagline, case number */}
            <section className="pb-12 lg:pb-16">
                <div className="st-container">
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-end">
                        <div className="lg:col-span-8">
                            <p className="eyebrow text-[var(--st-gold-deep)] mb-6">
                                Case {String(currentIndex + 1).padStart(2, '0')} · {cs.industry}
                            </p>
                            <motion.h1
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.9 }}
                                className="font-serif leading-[0.9] tracking-tighter"
                                style={{ fontSize: "clamp(2.6rem, 6.5vw, 6.5rem)" }}
                            >
                                {cs.client}
                            </motion.h1>
                            <p className="font-serif italic text-2xl md:text-3xl mt-5 max-w-3xl text-[var(--st-ink-soft)]">
                                {cs.tagline}
                            </p>
                        </div>
                    </div>
                </div>
            </section>

            {/* Full-bleed cover image — contained so brand posters render in full */}
            <section className="pb-12 lg:pb-16">
                <div className="st-container">
                    <div className="bg-[var(--st-ink)] flex items-center justify-center overflow-hidden">
                        <img src={cs.cover_image} alt={cs.client} className="max-w-full h-auto block max-h-[80vh]" />
                    </div>
                </div>
            </section>

            {/* Meta bar — client, industry, year, services, links — one uniform strip */}
            <section data-testid="work-meta-bar" className="border-y border-[var(--st-line)] bg-[var(--st-cream-soft)]">
                <div className="st-container py-8 lg:py-10">
                    <dl className="grid grid-cols-2 md:grid-cols-4 gap-8 lg:gap-6">
                        <div>
                            <dt className="eyebrow text-[var(--st-gold-deep)] mb-2">Client</dt>
                            <dd className="font-serif text-xl leading-tight">{cs.client}</dd>
                        </div>
                        <div>
                            <dt className="eyebrow text-[var(--st-gold-deep)] mb-2">Industry</dt>
                            <dd className="font-serif text-xl leading-tight">{cs.industry}</dd>
                        </div>
                        <div>
                            <dt className="eyebrow text-[var(--st-gold-deep)] mb-2">Timeline</dt>
                            <dd className="font-serif text-xl leading-tight">{cs.year}</dd>
                        </div>
                        <div>
                            <dt className="eyebrow text-[var(--st-gold-deep)] mb-2">Services</dt>
                            <dd className="text-sm leading-relaxed text-[var(--st-ink-soft)]">{(cs.services || []).length} disciplines</dd>
                        </div>
                    </dl>
                    {(cs.website || cs.instagram) && (
                        <div className="mt-8 pt-6 border-t border-[var(--st-line)] flex flex-wrap items-center gap-3">
                            <span className="eyebrow text-[var(--st-gold-deep)] mr-2">Visit —</span>
                            {cs.website && (
                                <a href={cs.website} target="_blank" rel="noopener noreferrer" data-testid="cs-visit-website" className="inline-flex items-center gap-2 px-4 py-2 border border-[var(--st-ink)] text-[var(--st-ink)] hover:bg-[var(--st-ink)] hover:text-[var(--st-cream)] text-xs uppercase tracking-widest transition-colors">
                                    <Globe size={14} /> Website
                                </a>
                            )}
                            {cs.instagram && (
                                <a href={cs.instagram} target="_blank" rel="noopener noreferrer" data-testid="cs-visit-instagram" className="inline-flex items-center gap-2 px-4 py-2 border border-[var(--st-ink)] text-[var(--st-ink)] hover:bg-[var(--st-ink)] hover:text-[var(--st-cream)] text-xs uppercase tracking-widest transition-colors">
                                    <Instagram size={14} /> Instagram
                                </a>
                            )}
                        </div>
                    )}
                </div>
            </section>

            {/* Services chip band */}
            {(cs.services || []).length > 0 && (
                <section className="py-16">
                    <div className="st-container">
                        <p className="eyebrow text-[var(--st-gold-deep)] mb-6">What we did</p>
                        <div className="flex flex-wrap gap-2">
                            {(cs.services || []).map((s) => (
                                <span key={s} className="text-xs uppercase tracking-widest border border-[var(--st-ink)] px-3.5 py-2 text-[var(--st-ink)]">
                                    {s}
                                </span>
                            ))}
                        </div>
                    </div>
                </section>
            )}

            {/* The story — sticky numbered navigator + editorial narrative */}
            <section data-testid="cs-story" className="py-16 lg:py-24 border-t border-[var(--st-line)]">
                <div className="st-container grid grid-cols-1 lg:grid-cols-12 gap-10">
                    <aside className="lg:col-span-3">
                        <div className="lg:sticky lg:top-32 space-y-6">
                            <p className="eyebrow text-[var(--st-gold-deep)]">The Story</p>
                            <ol className="space-y-3 text-sm">
                                <li className="flex gap-4"><span className="font-serif text-2xl text-[var(--st-gold)] leading-none">01</span><span className="font-serif text-xl">The Challenge</span></li>
                                <li className="flex gap-4"><span className="font-serif text-2xl text-[var(--st-gold)] leading-none">02</span><span className="font-serif text-xl">Our Approach</span></li>
                                <li className="flex gap-4"><span className="font-serif text-2xl text-[var(--st-gold)] leading-none">03</span><span className="font-serif text-xl">The Outcome</span></li>
                            </ol>
                        </div>
                    </aside>
                    <div className="lg:col-span-8 lg:col-start-5 space-y-16 lg:space-y-20">
                        <div>
                            <div className="flex items-baseline gap-4 mb-5">
                                <span className="font-serif text-4xl text-[var(--st-gold)]">01</span>
                                <h2 className="font-serif text-3xl md:text-4xl">The Challenge</h2>
                            </div>
                            <p className="text-lg lg:text-xl leading-relaxed text-[var(--st-ink-soft)]">{cs.challenge}</p>
                        </div>
                        <div>
                            <div className="flex items-baseline gap-4 mb-5">
                                <span className="font-serif text-4xl text-[var(--st-gold)]">02</span>
                                <h2 className="font-serif text-3xl md:text-4xl">Our Approach</h2>
                            </div>
                            <p className="text-lg lg:text-xl leading-relaxed text-[var(--st-ink-soft)]">{cs.approach}</p>
                        </div>
                        <div>
                            <div className="flex items-baseline gap-4 mb-5">
                                <span className="font-serif text-4xl text-[var(--st-gold)]">03</span>
                                <h2 className="font-serif text-3xl md:text-4xl">The Outcome</h2>
                            </div>
                            <p className="text-lg lg:text-xl leading-relaxed text-[var(--st-ink-soft)]">{cs.outcome}</p>
                        </div>
                    </div>
                </div>
            </section>

            {/* Metrics band */}
            {cs.metrics && cs.metrics.length > 0 && (
                <section data-testid="cs-metrics" className="section-dark py-20 lg:py-24">
                    <div className="st-container">
                        <p className="eyebrow text-[var(--st-gold)] mb-10">Impact, in numbers</p>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-8 lg:gap-10">
                            {cs.metrics.map((m, i) => (
                                <div key={i} className="border-t border-[rgba(235,233,222,0.2)] pt-6">
                                    <div className="stat-number text-[var(--st-gold)]">{m.value}</div>
                                    <p className="text-[11px] uppercase tracking-widest text-[#c8c5b3] mt-3">{m.label}</p>
                                </div>
                            ))}
                        </div>
                    </div>
                </section>
            )}

            {/* Editorial gallery — asymmetric magazine layout */}
            {cs.gallery && cs.gallery.length > 0 && (
                <section data-testid="cs-gallery" className="py-16 lg:py-24">
                    <div className="st-container">
                        <p className="eyebrow text-[var(--st-gold-deep)] mb-8">Selected artefacts</p>
                        <div className="space-y-6 lg:space-y-8">
                            {cs.gallery.map((g, i) => {
                                // Alternating asymmetric layout
                                const isFullBleed = i % 3 === 0;
                                const isRightHalf = i % 3 === 1;
                                const isLeftHalf = i % 3 === 2;
                                return (
                                    <motion.div
                                        key={i}
                                        initial={{ opacity: 0, y: 30 }}
                                        whileInView={{ opacity: 1, y: 0 }}
                                        viewport={{ once: true, margin: "-80px" }}
                                        transition={{ duration: 0.8 }}
                                        className={
                                            isFullBleed ? "w-full" :
                                            isRightHalf ? "md:w-10/12 md:ml-auto" :
                                            "md:w-10/12"
                                        }
                                    >
                                        <div className="overflow-hidden bg-[var(--st-ink)] flex items-center justify-center">
                                            <img
                                                src={g}
                                                alt={`${cs.client} — artefact ${i + 1}`}
                                                className="max-w-full h-auto block"
                                                loading="lazy"
                                            />
                                        </div>
                                        <p className="mt-3 eyebrow text-[var(--st-ink-soft)] opacity-70">Fig. {String(i + 1).padStart(2, '0')} — {cs.client}</p>
                                    </motion.div>
                                );
                            })}
                        </div>
                    </div>
                </section>
            )}

            {/* Next project — full bleed transition */}
            {next && (
                <section data-testid="cs-next" className="relative overflow-hidden">
                    <Link to={`/work/${next.slug}`} className="block relative group">
                        <div className="relative aspect-[21/9] min-h-[420px] overflow-hidden bg-[var(--st-ink)]">
                            <img
                                src={next.cover_image}
                                alt={next.client}
                                className="absolute inset-0 w-full h-full object-cover opacity-80 group-hover:opacity-100 group-hover:scale-[1.03] transition-all duration-[900ms]"
                            />
                            <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-black/50" />
                            <div className="relative z-10 h-full flex flex-col justify-center st-container text-[var(--st-cream)]">
                                <p className="eyebrow text-[var(--st-gold)] mb-4">Next case · {String((currentIndex + 1) % all.length + 1).padStart(2, '0')}</p>
                                <h2 className="font-serif leading-[0.92] tracking-tight" style={{ fontSize: "clamp(2.5rem, 6vw, 5.5rem)" }}>
                                    {next.client}
                                </h2>
                                <p className="font-serif italic text-xl md:text-2xl mt-3 max-w-2xl opacity-90">{next.tagline}</p>
                                <div className="mt-8 inline-flex items-center gap-2 text-sm uppercase tracking-widest group-hover:gap-4 transition-all">
                                    View case study <ArrowRight size={18} />
                                </div>
                            </div>
                        </div>
                    </Link>
                </section>
            )}

            {/* CTA */}
            <section className="py-20 lg:py-28 bg-[var(--st-cream)]">
                <div className="st-container flex flex-col md:flex-row md:items-center md:justify-between gap-8">
                    <h3 className="font-serif text-3xl md:text-4xl max-w-2xl leading-tight">
                        Working on something like this?<br/>
                        <span className="italic text-[var(--st-gold-deep)]">Let's talk.</span>
                    </h3>
                    <Link to="/contact" className="btn-primary shrink-0">
                        Start a project <ArrowUpRight size={16} />
                    </Link>
                </div>
            </section>
        </main>
    );
};

export default WorkDetail;
